import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const TransactionReviewPanel = ({ onBack }) => {
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState('');
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [followUp, setFollowUp] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const followUpRef = useRef(null);

  const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.xlsx'];
  const MAX_FILES = 10;

  const isAcceptedFile = (file) => {
    const name = file.name.toLowerCase();
    return ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext));
  };

  const addFiles = useCallback((newFiles) => {
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    const accepted = Array.from(newFiles).filter(file => {
      if (!isAcceptedFile(file)) return false;
      if (file.size > MAX_FILE_SIZE) {
        alert(`Rejected "${file.name}": file exceeds the 50 MB size limit.`);
        return false;
      }
      return true;
    });
    setFiles(prev => {
      const combined = [...prev, ...accepted];
      return combined.slice(0, MAX_FILES);
    });
  }, []);

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Stream a request to the backend
  const streamRequest = async (messageText, uploadFiles, conversationHistory) => {
    setIsStreaming(true);
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: messageText }]);
    // Add empty assistant message for streaming
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

    try {
      const formData = new FormData();
      formData.append('message', messageText);
      formData.append('conversation_history', JSON.stringify(conversationHistory));

      if (uploadFiles && uploadFiles.length > 0) {
        for (const f of uploadFiles) {
          formData.append('files', f);
        }
      } else {
        // Follow-up with no new files — send a dummy empty file since FastAPI requires at least one
        // Actually, we need the files from initial upload for context. The backend needs them.
        // For follow-ups, we re-send the original files.
        for (const f of files) {
          formData.append('files', f);
        }
      }

      const response = await fetch('/api/transaction-review/stream', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const err = await response.text();
        throw new Error(err);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed.startsWith('data: ')) {
            try {
              const data = JSON.parse(trimmed.slice(6));
              if (data.type === 'content' && data.text) {
                setMessages(prev => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  updated[updated.length - 1] = { ...last, content: last.content + data.text };
                  return updated;
                });
              } else if (data.type === 'error') {
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = {
                    ...updated[updated.length - 1],
                    content: `Error: ${data.error}`
                  };
                  return updated;
                });
              }
            } catch (e) {
              // skip parse errors
            }
          }
        }
      }
    } catch (err) {
      setMessages(prev => {
        const updated = [...prev];
        if (updated.length > 0 && updated[updated.length - 1].role === 'assistant') {
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: `Error: ${err.message}`
          };
        }
        return updated;
      });
    }

    setIsStreaming(false);
  };

  const handleBeginReview = async () => {
    if (files.length === 0 || !description.trim() || isStreaming) return;
    const fileNames = files.map(f => f.name).join(', ');
    const messageText = `Transaction review: ${description.trim()}\n\nFiles: ${fileNames}`;
    await streamRequest(messageText, files, []);
  };

  const handleFollowUp = async () => {
    if (!followUp.trim() || isStreaming) return;
    const text = followUp.trim();
    setFollowUp('');
    // Build conversation history from existing messages (exclude the current follow-up)
    const history = messages.map(m => ({ role: m.role, content: m.content }));
    await streamRequest(text, null, history);
  };

  const handleFollowUpKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleFollowUp();
    }
  };

  const handleNewReview = () => {
    setFiles([]);
    setDescription('');
    setMessages([]);
    setFollowUp('');
  };

  const hasStarted = messages.length > 0;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: 'var(--bg-primary)',
      overflow: 'hidden',
    }}>
      {/* Header bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 20px',
        borderBottom: '1px solid var(--border-color)',
        backgroundColor: 'var(--bg-secondary)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={onBack}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              color: 'var(--text-secondary)',
              padding: '2px 6px',
            }}
            title="Back to Chat"
          >
            &larr;
          </button>
          <h2 style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-body)',
          }}>
            Transaction Review
          </h2>
        </div>
        {hasStarted && (
          <button
            onClick={handleNewReview}
            disabled={isStreaming}
            style={{
              padding: '5px 14px',
              borderRadius: '16px',
              border: '1px solid var(--border-color)',
              background: 'transparent',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              fontFamily: 'var(--font-body)',
              cursor: isStreaming ? 'not-allowed' : 'pointer',
              opacity: isStreaming ? 0.5 : 1,
            }}
          >
            New Review
          </button>
        )}
      </div>

      {/* Upload section — shown before review starts */}
      {!hasStarted && (
        <div style={{
          padding: '24px 28px',
          maxWidth: '640px',
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setIsDragOver(false);
              if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${isDragOver ? 'var(--accent-color, #3b6b9a)' : 'var(--border-color, #ccc)'}`,
              borderRadius: '8px',
              padding: '28px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              backgroundColor: isDragOver
                ? 'rgba(59, 107, 154, 0.08)'
                : 'var(--bg-secondary, #fafafa)',
              marginBottom: '16px',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.xlsx"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files?.length) addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            <div style={{ fontSize: '28px', marginBottom: '6px', opacity: 0.5 }}>
              {isDragOver ? '\u2B07' : '\uD83D\uDCCE'}
            </div>
            <div style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              fontFamily: 'var(--font-body)',
            }}>
              {isDragOver ? 'Drop files here' : 'Drag & drop deal documents, or click to browse'}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              marginTop: '4px',
              fontFamily: 'var(--font-body)',
            }}>
              PDF, Word, or Excel ({files.length}/{MAX_FILES} files)
            </div>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {files.map((file, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-secondary)',
                  marginBottom: '4px',
                  fontSize: '13px',
                  fontFamily: 'var(--font-body)',
                  color: 'var(--text-primary)',
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {file.name.toLowerCase().endsWith('.pdf') ? '\uD83D\uDCC4' :
                     file.name.toLowerCase().endsWith('.xlsx') ? '\uD83D\uDCCA' : '\uD83D\uDCDD'}{' '}
                    {file.name}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      color: 'var(--text-tertiary)',
                      padding: '0 4px',
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                    title="Remove file"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Description textarea */}
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--text-primary)',
            marginBottom: '6px',
            fontFamily: 'var(--font-body)',
          }}>
            What are you looking for?
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the transaction and your concerns. Example: This is a 501(c)(3) hospital bond with three series including subordinate debt. I'm concerned about debt vs. equity characterization."
            style={{
              width: '100%',
              minHeight: '100px',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid var(--border-color)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              fontSize: '13px',
              fontFamily: 'var(--font-body)',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => e.target.style.borderColor = 'var(--accent-color, #3b6b9a)'}
            onBlur={(e) => e.target.style.borderColor = 'var(--border-color, #ccc)'}
          />

          {/* Begin Review button */}
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={handleBeginReview}
              disabled={files.length === 0 || !description.trim() || isStreaming}
              style={{
                padding: '9px 24px',
                borderRadius: '20px',
                border: 'none',
                background: files.length > 0 && description.trim()
                  ? 'var(--accent-color, #3b6b9a)'
                  : 'var(--text-tertiary, #ccc)',
                color: '#fff',
                fontSize: '13px',
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
                cursor: files.length > 0 && description.trim() ? 'pointer' : 'not-allowed',
                opacity: files.length > 0 && description.trim() ? 1 : 0.6,
              }}
            >
              Begin Review
            </button>
          </div>
        </div>
      )}

      {/* Conversation area — shown after review starts */}
      {hasStarted && (
        <>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
          }}>
            {messages.map((msg, idx) => (
              <div key={idx} style={{
                marginBottom: '16px',
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: msg.role === 'user' ? '80%' : '95%',
                  padding: msg.role === 'user' ? '10px 16px' : '0',
                  borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '0',
                  backgroundColor: msg.role === 'user' ? 'var(--bg-user-message, #1e3a5f)' : 'transparent',
                  color: msg.role === 'user' ? '#fff' : 'var(--text-primary)',
                  fontSize: '14px',
                  fontFamily: 'var(--font-body)',
                  lineHeight: '1.6',
                  whiteSpace: msg.role === 'user' ? 'pre-wrap' : undefined,
                }}>
                  {msg.role === 'assistant' ? (
                    <div className="markdown-content">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {msg.content || (isStreaming && idx === messages.length - 1 ? 'Analyzing documents...' : '')}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Follow-up input */}
          <div style={{
            padding: '12px 20px',
            borderTop: '1px solid var(--border-color)',
            backgroundColor: 'var(--bg-secondary)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <textarea
                ref={followUpRef}
                value={followUp}
                onChange={(e) => setFollowUp(e.target.value)}
                onKeyDown={handleFollowUpKeyPress}
                placeholder="Ask a follow-up question about this deal..."
                disabled={isStreaming}
                rows={1}
                style={{
                  flex: 1,
                  padding: '9px 12px',
                  borderRadius: '20px',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontSize: '13px',
                  fontFamily: 'var(--font-body)',
                  resize: 'none',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <button
                onClick={handleFollowUp}
                disabled={!followUp.trim() || isStreaming}
                style={{
                  padding: '9px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  background: followUp.trim() && !isStreaming
                    ? 'var(--accent-color, #3b6b9a)'
                    : 'var(--text-tertiary, #ccc)',
                  color: '#fff',
                  fontSize: '13px',
                  fontFamily: 'var(--font-body)',
                  cursor: followUp.trim() && !isStreaming ? 'pointer' : 'not-allowed',
                  opacity: followUp.trim() && !isStreaming ? 1 : 0.5,
                  flexShrink: 0,
                }}
              >
                Send
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TransactionReviewPanel;
